import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AppLayout from '../components/work/AppLayout';
import { OwnerPicker } from '../components/work/InitiativeOwners';
import { useOverlays } from '../components/work/overlay-context';
import { useProjectDecorations } from '../components/work/use-project-decorations';
import { useSession } from '../components/session-context';
import {
  createStreamTasks,
  StreamTasksEditor,
  streamPlansComplete,
  useDepartments,
  type StreamPlan,
} from '../components/work/InitiativeFields';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiFetch, v1Path } from '../lib/api';
import { fetchInitiativeWorkspace, initiativePath, INITIATIVES_PATH } from '../lib/initiatives';
import {
  fetchInitiativeTemplate,
  planRequirements,
  plansFromTemplate,
  type RequirementPlan,
  type TemplateRequirement,
  type TemplateStream,
} from '../lib/initiative-requirements';
import { cn } from '../lib/utils';
import type { MemberDto, WorkItemFull } from '../lib/work-types';

/** The template's business requirements, each with an owner and go-live date like a stream task. */
function RequirementsPlanEditor({
  requirements,
  members,
  value,
  disabled,
  onChange,
}: {
  requirements: TemplateRequirement[];
  members: MemberDto[];
  value: Record<string, RequirementPlan>;
  disabled: boolean;
  onChange: (value: Record<string, RequirementPlan>) => void;
}) {
  if (requirements.length === 0) {
    return <p className="text-sm text-muted-foreground">The template has no business requirements.</p>;
  }
  const set = (id: string, change: Partial<RequirementPlan>) => {
    const current = value[id] ?? { owner_ids: [], due_date: null };
    onChange({ ...value, [id]: { ...current, ...change } });
  };
  return (
    <ul className="divide-y rounded-lg border" aria-label="Business requirements">
      {requirements.map((entry) => {
        const plan = value[entry.id] ?? { owner_ids: [], due_date: null };
        return (
          <li key={entry.id} className="grid items-center gap-2 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto_9.5rem]">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{entry.title}</p>
              {entry.description ? <p className="truncate text-xs text-muted-foreground">{entry.description}</p> : null}
            </div>
            <OwnerPicker
              label={entry.title}
              className="min-h-8 py-0.5 text-xs"
              value={plan.owner_ids}
              members={members}
              disabled={disabled}
              onChange={(owner_ids) => set(entry.id, { owner_ids })}
            />
            <Input
              type="date"
              aria-label={`${entry.title} go-live date`}
              title="Go-live date (optional)"
              value={plan.due_date ?? ''}
              disabled={disabled}
              onChange={(event) => set(entry.id, { due_date: event.target.value || null })}
            />
          </li>
        );
      })}
    </ul>
  );
}

function Step({
  number,
  title,
  hint,
  done,
  children,
}: {
  number: number;
  title: string;
  hint: string;
  done: boolean;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border bg-card p-5 text-card-foreground">
      <div className="mb-4 flex items-start gap-3">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
          )}
          aria-hidden
        >
          {number}
        </span>
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{hint}</p>
        </div>
      </div>
      <div className="sm:pl-10">{children}</div>
    </section>
  );
}

export default function NewInitiative() {
  const navigate = useNavigate();
  const { toast, confirm } = useOverlays();
  const { ready: sessionReady, isAdmin: isSystemAdmin } = useSession();
  const [title, setTitle] = useState('');
  const [launchDate, setLaunchDate] = useState('');
  const [plans, setPlans] = useState<StreamPlan[]>([]);
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | undefined>();
  const { members } = useProjectDecorations(workspaceId);
  const [busy, setBusy] = useState(false);
  const [requirements, setRequirements] = useState<TemplateRequirement[] | null>(null);
  const [templateError, setTemplateError] = useState('');
  const [requirementPlans, setRequirementPlans] = useState<Record<string, RequirementPlan>>({});
  const [templateStreams, setTemplateStreams] = useState<TemplateStream[] | null>(null);
  const [startingPlans, setStartingPlans] = useState<StreamPlan[] | null>(null);
  const [missingStreams, setMissingStreams] = useState<TemplateStream[]>([]);
  const departments = useDepartments();

  useEffect(() => {
    if (!sessionReady || !isSystemAdmin) return;
    let cancelled = false;
    fetchInitiativeWorkspace()
      .then((workspace) => {
        if (!cancelled) setWorkspaceId(workspace.project.id);
      })
      .catch(() => undefined);
    fetchInitiativeTemplate()
      .then((template) => {
        if (cancelled) return;
        setRequirements(template.requirements);
        setTemplateStreams(template.streams);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTemplateError(err instanceof Error ? err.message : 'Could not load the form');
      });
    return () => {
      cancelled = true;
    };
  }, [sessionReady, isSystemAdmin]);

  useEffect(() => {
    if (startingPlans || !templateStreams || departments.length === 0) return;
    const { plans: planned, missing } = plansFromTemplate(templateStreams, departments);
    setStartingPlans(planned);
    setMissingStreams(missing);
    setPlans((current) => (current.length === 0 ? planned : current));
  }, [departments, startingPlans, templateStreams]);

  const plansReady = streamPlansComplete(plans);
  const ready = title.trim() !== '' && plansReady;
  const plannedRequirements = Object.entries(requirementPlans).filter(
    ([, plan]) => plan.owner_ids.length > 0 || plan.due_date,
  );
  const dirty =
    title.trim() !== '' ||
    launchDate !== '' ||
    JSON.stringify(plans) !== JSON.stringify(startingPlans ?? []) ||
    ownerIds.length > 0 ||
    plannedRequirements.length > 0;

  async function cancel() {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard this initiative?',
        description: 'Nothing has been created yet.',
        confirmLabel: 'Discard',
        destructive: true,
      });
      if (!ok) return;
    }
    navigate(INITIATIVES_PATH);
  }

  async function submit() {
    if (!ready) return;
    setBusy(true);
    try {
      const workspace = await fetchInitiativeWorkspace();
      const projectId = workspace.project.id;
      const created = await apiFetch<WorkItemFull>(v1Path(`/projects/${projectId}/work-items`), {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          type: 'initiative',
          assignee_ids: ownerIds,
          due_date: launchDate || null,
        }),
      });
      const missed: string[] = [];
      let taskCount = 0;
      try {
        const result = await planRequirements(created.id, workspace.requirements.project.id, requirements ?? [], requirementPlans);
        taskCount += result.count;
        if (result.failed.length) missed.push(`the owners or dates for ${result.failed.join(', ')}`);
      } catch (err) {
        missed.push(`the business requirement owners and dates (${err instanceof Error ? err.message : 'unknown error'})`);
      }
      if (plans.length) {
        try {
          taskCount += (await createStreamTasks(created.id, plans)).keys.length;
        } catch (err) {
          missed.push(`the tasks (${err instanceof Error ? err.message : 'unknown error'})`);
        }
      }
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
      if (missed.length) {
        toast(`Created ${created.key}, but ${missed.join(' and ')} were not saved. Add them on this page.`);
        navigate(initiativePath(created.id));
        return;
      }
      toast(`Created ${created.key} with ${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}.`);
      navigate(`${INITIATIVES_PATH}?layout=backlog`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not create the initiative');
      setBusy(false);
    }
  }

  if (sessionReady && !isSystemAdmin) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-3xl space-y-3">
          <h1 className="text-2xl font-semibold">New initiative</h1>
          <p className="text-sm">Only system admins can create initiatives. Anyone can fill one in once it exists.</p>
          <Link to={INITIATIVES_PATH} className="text-sm">
            Back to Initiatives
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
        <Link
          to={INITIATIVES_PATH}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground no-underline hover:text-foreground hover:no-underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Initiatives
        </Link>
        <h1 className="text-2xl font-semibold">New initiative</h1>

        <Step number={1} title="Initiative" hint="What is launching and when." done={title.trim() !== ''}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="ni-title">Title</Label>
              <Input
                id="ni-title"
                value={title}
                autoFocus
                placeholder="e.g. Retail Media Network"
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Owners</Label>
              <div>
                <OwnerPicker value={ownerIds} members={members} onChange={setOwnerIds} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ni-launch">Target launch</Label>
              <Input
                id="ni-launch"
                type="date"
                className="w-48"
                value={launchDate}
                onChange={(event) => setLaunchDate(event.target.value)}
              />
            </div>
          </div>
        </Step>

        <Step
          number={2}
          title="Business requirements"
          hint="Every initiative gets these as tasks in its launch plan. Give them owners and dates now or later."
          done={plannedRequirements.length > 0}
        >
          {requirements ? (
            <RequirementsPlanEditor
              requirements={requirements}
              members={members}
              value={requirementPlans}
              disabled={busy}
              onChange={setRequirementPlans}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{templateError || 'Loading the business requirements…'}</p>
          )}
        </Step>

        <Step
          number={3}
          title="Streams & additional tasks"
          hint="Any initiative-specific work goes here, by stream: Sales, Software, Operations, Finance, Legal, Marketing and so on."
          done={plansReady}
        >
          <div className="space-y-3">
            {missingStreams.length ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                {missingStreams.map((entry) => entry.stream).join(', ')}{' '}
                {missingStreams.length === 1 ? 'is' : 'are'} in the template but not a stream in Pulse, so{' '}
                {missingStreams.length === 1 ? 'its tasks were' : 'their tasks were'} left out:{' '}
                {missingStreams.flatMap((entry) => entry.tasks).join(', ')}.
              </p>
            ) : null}
            <StreamTasksEditor value={plans} onChange={setPlans} />
          </div>
        </Step>

        <div className="sticky bottom-0 z-20 flex justify-end gap-2 rounded-t-xl border bg-background/95 px-4 py-3 shadow-sm backdrop-blur">
          <Button type="button" variant="outline" disabled={busy} onClick={() => void cancel()}>
            Cancel
          </Button>
          <Button type="button" disabled={!ready || busy} onClick={() => void submit()}>
            Create
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
